/**
 * Pure SCROLL math for ScrollList — clamping + momentum, free of Phaser/DOM so the
 * touch-drag + wheel behavior is unit-testable. The widget feeds pointer/wheel
 * deltas in and reads back a clamped offset.
 */

/** The max scroll offset (>= 0) for a content taller than its viewport. */
export function maxScroll(contentHeight: number, viewportHeight: number): number {
  return Math.max(0, contentHeight - viewportHeight);
}

/** Clamp a scroll offset to [0, maxScroll]. */
export function clampScroll(offset: number, contentHeight: number, viewportHeight: number): number {
  const max = maxScroll(contentHeight, viewportHeight);
  if (offset < 0) return 0;
  if (offset > max) return max;
  return offset;
}

export interface MomentumState {
  /** Current scroll offset (px from top). */
  offset: number;
  /** Current velocity (px/ms), decays each tick. */
  velocity: number;
}

/** Per-ms velocity decay factor (gentle glide, calm). */
export const SCROLL_FRICTION = 0.92;

/** Below this speed (px/ms) momentum stops, so it settles cleanly. */
export const SCROLL_MIN_VELOCITY = 0.01;

/**
 * Advance a flinging list by `deltaMs`: apply velocity, decay it, and clamp to
 * bounds (killing velocity at the edges so it doesn't fight the clamp). Mutates +
 * returns the state.
 */
export function stepMomentum(
  s: MomentumState,
  deltaMs: number,
  contentHeight: number,
  viewportHeight: number,
): MomentumState {
  if (Math.abs(s.velocity) < SCROLL_MIN_VELOCITY) {
    s.velocity = 0;
    return s;
  }
  s.offset += s.velocity * deltaMs;
  s.velocity *= Math.pow(SCROLL_FRICTION, deltaMs / 16);
  const clamped = clampScroll(s.offset, contentHeight, viewportHeight);
  if (clamped !== s.offset) {
    s.offset = clamped;
    s.velocity = 0;
  }
  return s;
}

/** True when content overflows its viewport (a scrollbar / drag is meaningful). */
export function isScrollable(contentHeight: number, viewportHeight: number): boolean {
  return contentHeight > viewportHeight;
}

/**
 * Movement (px) a pointer must travel from its press point before a press becomes a
 * DRAG rather than a TAP. ~8px is the conventional touch slop: small enough that a
 * deliberate drag is recognized immediately, large enough that a fat-thumb tap (which
 * always wobbles a pixel or two) still reads as a tap and selects the row under it.
 */
export const DRAG_THRESHOLD = 8;

/**
 * Has the pointer moved far enough from its press point to count as a DRAG? Pure +
 * unit-testable so the gesture rule (the heart of "a drag that starts over a row still
 * scrolls, a tap on a row still selects") is verified without a live Phaser pointer.
 * Compares the squared distance to the squared threshold (no sqrt).
 */
export function isDragGesture(
  startX: number,
  startY: number,
  x: number,
  y: number,
  threshold: number = DRAG_THRESHOLD,
): boolean {
  const dx = x - startX;
  const dy = y - startY;
  return dx * dx + dy * dy > threshold * threshold;
}

/**
 * Scrollbar thumb geometry (top + height in viewport px) for a given offset, or
 * null when not scrollable. Used to draw a faint, non-interactive indicator.
 */
export function thumbGeometry(
  offset: number,
  contentHeight: number,
  viewportHeight: number,
): { top: number; height: number } | null {
  if (!isScrollable(contentHeight, viewportHeight)) return null;
  const ratio = viewportHeight / contentHeight;
  const height = Math.max(24, viewportHeight * ratio);
  const max = maxScroll(contentHeight, viewportHeight);
  const travel = viewportHeight - height;
  const top = max === 0 ? 0 : (offset / max) * travel;
  return { top, height };
}
